/**
 * DangoteContext — Single source of truth for all Dangote workspace pages.
 *
 * Architecture: Monthly Reporting → Asset Calculation → BU Aggregation → Corporate
 *
 * ONE fetch per period change.
 * All downstream pages read from useDangote() — never fetch independently.
 * This ensures:
 *   - Zero conflicting data states across pages
 *   - No duplicate API calls
 *   - Instant page navigation (data already loaded)
 *   - Period selector updates ALL pages simultaneously
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import client from "@/api/client";

const DangoteContext = createContext(null);

export function DangoteProvider({ children }) {
  const [periods,        setPeriods]        = useState([]);
  const [selectedPeriod, setSelectedPeriodState] = useState(null);
  const [consolidated,   setConsolidated]   = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [importing,      setImporting]      = useState(false);
  const [importResult,   setImportResult]   = useState(null);
  // VB cache: keyed by period_id — shared by ValueBridge, Strategy, Structuring, Financing
  const [vbCache,        setVbCache]        = useState({});
  const [vbLoading,      setVbLoading]      = useState(false);

  // Load periods on mount — default to Jan 2026 (approved) for demo
  useEffect(() => {
    client.get("/dangote/periods").then(r => {
      const ps = r.data || [];
      setPeriods(ps);
      // Priority: approved → submitted → draft → first available
      const active =
        ps.find(p => p.status === "approved") ||
        ps.find(p => p.status === "submitted") ||
        ps.find(p => p.status === "draft")    ||
        ps[0];
      if (active) setSelectedPeriodState(active.period_id);
    }).catch(() => setLoading(false));
  }, []);

  // Load consolidated data when period changes — canonical source for ALL pages
  const loadConsolidated = useCallback((periodId) => {
    if (!periodId) return;
    setLoading(true);
    client.get(`/dangote/consolidated/${periodId}`)
      .then(r => { setConsolidated(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedPeriod) loadConsolidated(selectedPeriod);
  }, [selectedPeriod, loadConsolidated]);

  const setSelectedPeriod = useCallback((periodId) => {
    setSelectedPeriodState(periodId);
  }, []);

  // Refresh after actions (upload, calculate, transition)
  const refresh = useCallback(() => {
    if (selectedPeriod) loadConsolidated(selectedPeriod);
  }, [selectedPeriod, loadConsolidated]);

  // Load Value Bridge data (cached per period, shared across all pipeline pages)
  const loadVb = useCallback((periodId) => {
    if (!periodId) return;
    if (vbCache[periodId]) return; // Already cached — no duplicate fetch
    setVbLoading(true);
    client.get(`/dangote/value-bridge/${periodId}`)
      .then(r => {
        setVbCache(prev => ({ ...prev, [periodId]: r.data }));
        setVbLoading(false);
      })
      .catch(() => setVbLoading(false));
  }, [vbCache]);

  // Preload VB data when period changes
  useEffect(() => {
    if (selectedPeriod) loadVb(selectedPeriod);
  }, [selectedPeriod, loadVb]);

  // Invalidate VB cache for a period (called after state machine transitions)
  const invalidateVb = useCallback((periodId) => {
    const pid = periodId || selectedPeriod;
    if (!pid) return;
    setVbLoading(true);
    client.get(`/dangote/value-bridge/${pid}`)
      .then(r => {
        setVbCache(prev => ({ ...prev, [pid]: r.data }));
        setVbLoading(false);
      })
      .catch(() => setVbLoading(false));
  }, [selectedPeriod]);

  // Upload Excel/CSV — triggers recalculation automatically
  const uploadFile = useCallback(async (file, periodId) => {
    setImporting(true);
    setImportResult(null);
    try {
      const isCSV = file.name.endsWith(".csv");
      const resp = await client.post(
        `/dangote/import/excel/${periodId || selectedPeriod}`,
        file,
        { headers: { "Content-Type": isCSV ? "text/csv" : "application/octet-stream" } }
      );
      setImportResult(resp.data);
      // Refresh consolidated after import
      setTimeout(() => {
        if (periodId || selectedPeriod) loadConsolidated(periodId || selectedPeriod);
      }, 500);
      return resp.data;
    } catch (e) {
      const err = e.response?.data?.detail || "Upload failed";
      setImportResult({ status: "error", message: err });
      return null;
    } finally {
      setImporting(false);
    }
  }, [selectedPeriod, loadConsolidated]);

  // Transition period status
  const transitionPeriod = useCallback(async (periodId, newStatus) => {
    await client.post(`/dangote/periods/${periodId}/transition`, { status: newStatus });
    // Refresh periods list
    const r = await client.get("/dangote/periods");
    setPeriods(r.data || []);
    // Refresh consolidated
    loadConsolidated(periodId);
  }, [loadConsolidated]);

  // Helpers derived from consolidated data
  const period      = consolidated?.period;
  const totals      = consolidated?.totals;
  const byBu        = consolidated?.by_bu || [];
  const topEmitters = consolidated?.top_emitters || [];
  const opportunities = consolidated?.opportunities || {};
  const completeness  = consolidated?.data_completeness_pct || 0;
  const vrsScore      = consolidated?.vrs_score || 0;
  const methodology   = consolidated?.methodology;

  return (
    <DangoteContext.Provider value={{
      // Core state
      periods, selectedPeriod, setSelectedPeriod,
      consolidated, loading, refresh,
      // Derived helpers
      period, totals, byBu, topEmitters, opportunities, completeness, vrsScore, methodology,
      // Import
      importing, importResult, uploadFile, setImportResult,
      // Actions
      transitionPeriod,
      // VB cache (shared across ValueBridge, Strategy, Structuring, Financing)
      vbCache, vbLoading, invalidateVb,
    }}>
      {children}
    </DangoteContext.Provider>
  );
}

export const useDangote = () => {
  const ctx = useContext(DangoteContext);
  if (!ctx) throw new Error("useDangote must be used within DangoteProvider");
  return ctx;
};
